import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Calendar, Palette, Loader2 } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';
import { track } from '@/lib/analytics';
import '@/styles/app-states.css';
import '@/styles/forms.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const [executing, setExecuting] = useState(true);
  const [success, setSuccess] = useState(false);
  const [addOns, setAddOns] = useState({});
  const [error, setError] = useState('');
  //: No purchase to confirm. NOT an error — nothing went wrong, the visitor
  //  simply arrived at this URL without having bought anything.
  const [noPurchase, setNoPurchase] = useState(false);

  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    const gateway = searchParams.get('gateway');

    /* ── A SUCCESS STATE NOW REQUIRES EVIDENCE OF A PURCHASE ────────────────
     *
     * This page used to render "Payment Confirmed!" for a BARE visit: with no
     * query parameters at all it set success = true and told anyone who typed
     * the URL that their purchase had gone through. That is why
     * `purchase_completed` was wired to the two verified payment responses
     * rather than to this page's success state — the analytics event was
     * routed around a defect instead of the defect being fixed.
     *
     * THE PERMISSIVE BRANCH EXISTED FOR A REAL CASE, which is why it cannot
     * simply be deleted: the `finally` below calls
     * `history.replaceState(..., '/thank-you')` to clean the payment ids out
     * of the URL, so a REFRESH after a genuine purchase arrives here with no
     * parameters. Removing the branch would show a confirmed customer an
     * error on reload.
     *
     * So the branch stays and gains a requirement. The evidence a refresh
     * carries is `pending_payment_id` in sessionStorage, written on every
     * confirmed path below and by the promo path above. Same session, same
     * tab, written only after a purchase resolved.
     */
    // Promo / 0-charge checkout — no PayPal execution needed, just store the id.
    // A promo REDIRECT always carries its paymentId; `gateway=promo` alone,
    // typed by hand, is not a purchase and no longer renders one.
    if (gateway === 'promo') {
      if (!paymentId) {
        setExecuting(false);
        setNoPurchase(true);
        return;
      }
      sessionStorage.setItem('pending_payment_id', paymentId);
      setExecuting(false);
      setSuccess(true);
      window.history.replaceState({}, '', '/thank-you');
      return;
    }

    if (!paymentId || !payerId) {
      setExecuting(false);
      // The refresh case: this tab completed a purchase moments ago and the
      // URL was cleaned. Anything else is somebody who just typed the URL.
      if (sessionStorage.getItem('pending_payment_id')) {
        setSuccess(true);
      } else {
        setNoPurchase(true);
      }
      return;
    }

    const executePayment = async () => {
      try {
        const resp = await axios.post(`${API}/payments/execute-payment`, {
          paymentId,
          PayerID: payerId,
        }, { withCredentials: true });

        if (resp.data.status === 'success') {
          // CONFIRMED SUCCESS. Firing on this response rather than on the
          // `success` flag was originally a workaround for the bare-visit
          // defect fixed above; it stays because it is also just correct —
          // the event should follow the payment gateway's answer, not a
          // component's render state.
          //
          // NO value: execute-payment returns {status, payment_id, plan,
          // message} and no amount (paypal_routes.py:479-484). A hardcoded
          // 49 would be wrong on the add-on and promo paths, so this sends
          // the conversion without a figure rather than a made-up figure.
          track('purchase_completed', { gateway: 'paypal', plan: 'STARTER' });
          // Store payment_id so NewAuditPage can skip the payment modal
          sessionStorage.setItem('pending_payment_id', paymentId);
          // Fetch user data to find purchased add-ons
          try {
            const meResp = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setAddOns(meResp.data.add_ons || {});
          } catch (_) {}
          setSuccess(true);
        } else {
          setError('Payment could not be confirmed. Please contact support.');
        }
      } catch (e) {
        console.error('Payment execution failed:', e);
        setError('Payment verification failed. Please contact support at hello@uxauditpro.com');
      } finally {
        setExecuting(false);
        // Clean URL
        window.history.replaceState({}, '', '/thank-you');
      }
    };

    executePayment();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20">
      <SEO
        title="Thank You"
        description="Your UXAuditPro Starter purchase is confirmed."
        canonical="/thank-you"
        noIndex={true}
      />

      <div className="w-full max-w-lg">
        {executing ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: 'var(--accent-text)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
              Confirming your payment…
            </p>
          </motion.div>
        ) : noPurchase ? (
          /* NOT an error state, and not a success state. Nothing failed; there
             is simply no purchase attached to this visit. It says so plainly
             and offers the two things such a visitor actually wants. */
          <motion.div
            initial={{ y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-8 text-center"
            style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
            data-testid="thankyou-no-purchase"
          >
            <h1
              className="mb-2"
              style={{ fontSize: 'var(--type-h2-size)', fontWeight: 'var(--type-h2-weight)', lineHeight: 'var(--type-h2-leading)', letterSpacing: 'var(--type-h2-tracking)', color: 'var(--ink-1)' }}
            >
              Nothing to confirm
            </h1>
            <p className="text-sm mb-6" style={{ color: 'var(--ink-2)' }}>
              This page confirms a completed purchase, and there is no purchase
              attached to this visit. If you have just paid and landed here by
              mistake, your receipt email has the details.
            </p>
            <Link to="/audit/new" className="uxa-submit uxa-submit--block">
              Start an audit
              <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
            </Link>
            <p className="text-center text-xs mt-4" style={{ color: 'var(--ink-2)' }}>
              <Link to="/pricing/" style={{ color: 'var(--link)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                See pricing
              </Link>
            </p>
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-8 text-center"
            style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
          >
            <p className="font-semibold mb-4" style={{ color: 'var(--danger)' }}>{error}</p>
            <Link
              to="/audit/new"
              className="text-sm font-semibold"
              style={{ color: 'var(--link)', textDecoration: 'underline', textUnderlineOffset: '3px' }}
            >
              Start Auditing →
            </Link>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Success Card */}
            <div className="uxa-app-card mb-4">
              <div className="uxa-form-card-spectrum" aria-hidden="true" />
              {/* Header */}
              <div className="p-8 text-center" style={{ background: 'var(--accent-text)' }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <CheckCircle className="w-9 h-9" style={{ color: 'white' }} />
                </div>
                <h1 className="mb-1" style={{ fontSize: 'var(--type-h2-size)', fontWeight: 'var(--type-h2-weight)', lineHeight: 'var(--type-h2-leading)', letterSpacing: 'var(--type-h2-tracking)', color: 'var(--surface-white)' }}>
                  Payment Confirmed!
                </h1>
                <p className="text-sm" style={{ color: 'var(--surface-white)' }}>
                  Thank you for purchasing the Starter plan.
                </p>
              </div>

              {/* What's included */}
              <div className="p-8">
                <p className="uxa-eyebrow mb-4">
                  Your Starter Audit includes
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    'Full detailed UX report',
                    'PDF download',
                    'Annotated screenshots',
                    'Fix recommendations',
                    'Severity prioritization',
                  ].map((feat) => (
                    <li key={feat} className="flex items-center gap-3 text-sm" style={{ color: 'var(--ink-2)' }}>
                      <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--success)' }} />
                      {feat}
                    </li>
                  ))}
                </ul>

                {/* Purchased add-ons */}
                {(addOns.white_label || addOns.expert_call) && (
                  <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-1)' }}>
                    <p className="uxa-eyebrow mb-3">
                      Add-ons purchased
                    </p>
                    {addOns.white_label && (
                      <div className="flex items-center gap-3 text-sm mb-2" style={{ color: 'var(--ink-2)' }}>
                        <Palette className="w-4 h-4" style={{ color: 'var(--spectrum-indigo)' }} aria-hidden="true" />
                        White Label Reports — branding activated
                      </div>
                    )}
                    {addOns.expert_call && (
                      <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--ink-2)' }}>
                        <Calendar className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
                        30-min Expert Consultation — check your email to schedule
                      </div>
                    )}
                  </div>
                )}

                <Link to="/audit/new" className="uxa-submit uxa-submit--block">
                  Start Your Audit
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </Link>
              </div>
            </div>

            <p className="text-center text-xs" style={{ color: 'var(--ink-2)' }}>
              A receipt has been sent to your email.{' '}
              <a href="mailto:hello@uxauditpro.com" className="underline" style={{ color: 'var(--accent-text)' }}>
                Need help?
              </a>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
